"use client";
import { useEffect, useRef, useState } from "react";
import { HwpViewerClient } from "./HwpViewerClient";
import Icon from "./Icon";

// 공고문 파일 인라인 뷰어 — PDF(원본/HWP→PDF 변환본)는 iframe, 변환 없는 HWP 는 WASM 뷰어.
//   헤더/링크 없이 뷰어만, 우상단에 기능버튼(전체화면·다운로드) 오버레이.
export function FileViewer({
  fileId,
  fileType,
  pdfPath,
  origName,
}: {
  fileId: number;
  fileType: string | null;
  pdfPath: string | null;
  origName?: string | null;
}) {
  const isPdf = fileType === "pdf";
  const isHwp = fileType === "hwp" || fileType === "hwpx";
  // PDF 원본이 아니면서 변환 PDF(pdfPath)가 있는 모든 포맷(hwp·hwpx·docx·doc·pptx·xlsx…)은 그 PDF 로 렌더.
  const hasConvertedPdf = !isPdf && !!pdfPath;
  const isHwpNoPdf = isHwp && !pdfPath; // 변환본 없는 hwp 만 WASM 뷰어 폴백
  const supported = isPdf || hasConvertedPdf || isHwpNoPdf;

  const ref = useRef<HTMLDivElement>(null);
  const [fs, setFs] = useState(false);
  // 리사이즈 후 PDF 뷰어 재적합용 — Chrome 임베드 PDF 는 폭 변경 시 자동 refit 안 돼(가로 스크롤만 생김) →
  //   리사이즈가 끝나면 iframe 을 1회 리마운트(key 변경)해 #zoom=page-width 를 새 폭으로 다시 적용한다.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const onChange = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // 모바일 판정 — 모바일 브라우저(iOS Safari/안드로이드 Chrome)는 PDF 를 <iframe> 임베드로 렌더하지 못해
  //   빈 화면이 된다("공고문이 안 보임"). 모바일에선 iframe 대신 '새 탭에서 열기' 카드로 폴백한다.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // 컨테이너 폭 변화 감지 → 디바운스 후 뷰어 리로드(드래그가 멈춘 뒤 1회). PDF(iframe) 일 때만.
  useEffect(() => {
    if (!(isPdf || hasConvertedPdf)) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let lastW = el.clientWidth;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      // 탭 전환 시 공고문 패널이 display:none → clientWidth 0 이 됐다가 복귀하면 폭이 0↔실제로 튀어
      //   '리사이즈'로 오인 → iframe 리마운트(PDF 재로드)로 보였다. 폭 0(숨김 상태)은 무시하면
      //   복귀 시 lastW(=이전 실제 폭)와 같아 재로드 안 함. 실제 리사이저 드래그(보이는 상태)만 반영.
      if (w === 0) return;
      if (Math.abs(w - lastW) < 8) return; // 미세 변화 무시(깜빡임 방지)
      lastW = w;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setReloadKey((k) => k + 1), 280); // 리사이즈 종료 후 1회만 리로드
    });
    ro.observe(el);
    return () => { ro.disconnect(); if (timer) clearTimeout(timer); };
  }, [isPdf, hasConvertedPdf]);

  if (!supported) return null;

  const src = isPdf ? `/api/support-files/${fileId}/raw` : `/api/support-files/${fileId}/raw?pdf=1`;
  const showPdf = isPdf || hasConvertedPdf;

  const toggleFs = () => {
    const el = ref.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  };

  const btn: React.CSSProperties = {
    width: 36, height: 36, borderRadius: 9, border: "1px solid #e4e8f6",
    background: "rgba(255,255,255,.92)", cursor: "pointer", display: "inline-flex",
    alignItems: "center", justifyContent: "center", color: "#2C5CFF",
    boxShadow: "0 2px 8px rgba(5,26,62,.12)",
  };

  // 모바일 + PDF: iframe 임베드가 빈 화면이 되므로, '새 탭에서 열기 + 다운로드' 카드로 대체.
  if (isMobile && showPdf) {
    return (
      <div
        ref={ref}
        style={{
          background: "#fff", borderRadius: 14, border: "1px solid #e4e8f6", padding: "32px 20px",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 14, textAlign: "center", minHeight: 320,
        }}
      >
        <Icon name="description" size={44} />
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0E1726", wordBreak: "break-all", lineHeight: 1.45, maxWidth: 420 }}>
          {origName ?? "공고문"}
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
          모바일에서는 공고문을 새 탭에서 열어 보실 수 있습니다.
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", marginTop: 4 }}>
          <a
            href={`${src}#toolbar=1`}
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#2C5CFF", color: "#fff", borderRadius: 10, padding: "11px 20px", fontSize: 14.5, fontWeight: 700, textDecoration: "none" }}
          >
            <Icon name="open_in_new" size={18} />공고문 열기
          </a>
          <a
            href={`/api/support-files/${fileId}/raw?dl=1`}
            download
            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#fff", color: "#2C5CFF", border: "1px solid #c7d3ff", borderRadius: 10, padding: "11px 18px", fontSize: 14.5, fontWeight: 700, textDecoration: "none" }}
          >
            <Icon name="download" size={18} />다운로드
          </a>
        </div>
      </div>
    );
  }

  // 비전체화면 높이: PDF 는 '현재 폭 기준 1페이지(A4 세로)'가 꽉 차게 aspectRatio(595:842)로 산출 →
  //   한 화면에 정확히 1페이지만 보인다(1.5페이지·부분2페이지·여백 방지). 폭을 리사이저로 줄이면 높이도 함께 준다.
  //   WASM hwp(변환본 없는 hwp) 뷰어는 스크롤 콘텐츠라 aspectRatio 대신 기본 높이.
  const boxSize: React.CSSProperties = fs
    ? { height: "100vh" }
    : showPdf
    ? { width: "100%", aspectRatio: "595 / 842" }
    : { height: "min(80vh, 900px)" };
  return (
    <div ref={ref} style={{ position: "relative", background: "#fff", borderRadius: 14, overflow: "hidden", border: "1px solid #e4e8f6", display: "flex", flexDirection: "column", minHeight: 0, ...boxSize }}>
      {/* 기능버튼 오버레이 (뷰어 내부 우상단) — 헤더 없이 뷰어 위에 떠 있음 */}
      <div style={{ position: "absolute", top: 10, right: 10, zIndex: 2, display: "flex", gap: 8 }}>
        {showPdf && (
          // 임베드(iframe) PDF 뷰어는 툴바가 축소돼 검색(돋보기)이 없음 → 새 탭의 전체 PDF 뷰어로 열면 Ctrl+F·돋보기 검색 가능.
          <a href={`${src}#toolbar=1`} target="_blank" rel="noreferrer" title="새 탭에서 열기 (검색·돋보기)" style={{ ...btn, width: "auto", padding: "0 12px", gap: 6, textDecoration: "none", fontSize: 13, fontWeight: 700 }}>
            <Icon name="search" size={18} />검색
          </a>
        )}
        <button type="button" onClick={toggleFs} title={fs ? "전체화면 종료" : "전체화면"} style={btn}>
          <Icon name={fs ? "close_fullscreen" : "open_in_full"} size={20} />
        </button>
        <a href={`/api/support-files/${fileId}/raw?dl=1`} title="다운로드" download style={{ ...btn, textDecoration: "none" }}>
          <Icon name="download" size={20} />
        </a>
      </div>

      {showPdf ? (
        <iframe
          key={reloadKey} // 리사이즈 종료 시 리마운트 → 새 폭으로 zoom=page-width 재적용
          // #navpanes=0: Chrome PDF 뷰어 왼쪽 페이지 썸네일/북마크 패널 숨김(임베드는 본문만). toolbar 는 유지.
          // #zoom=page-width: 문서를 뷰어 '폭'에 맞춰 렌더 → 패널(리사이저)로 영역을 넓히/좁히면 공고문도 함께
          //   늘어나/줄어든다(고정 배율이면 여백/가로스크롤이 생기던 것 개선). 'page-width' 는 fit 모드라 리사이즈 시 재적합.
          src={`${src}#navpanes=0&zoom=page-width`}
          title={origName ?? "공고문"}
          style={{ width: "100%", height: "100%", minHeight: 0, border: "none", background: "#fff", display: "block", flex: 1 }}
        />
      ) : (
        <div style={{ padding: "12px 4px", flex: 1, minHeight: 0, overflow: "auto" }}>
          <HwpViewerClient fileId={fileId} />
        </div>
      )}
    </div>
  );
}
